/**
 * The policy vocabulary the screens speak.
 *
 * Two rules from `docs/07-ui-screens.md` drive everything here:
 *
 *   - **Blocked is not the same as flagged.** A zero pool is a wall; below-50% participation is
 *     a guideline the manager may fall short of with a reason (D-21). For a 370-person
 *     department the shortfall is *every time*, so copy that reads as failure would teach those
 *     managers the app forbids what the policy permits.
 *   - **Every blocked action names the rule**, in plain language plus the id.
 */
import type { ClaimStatus, Issue, ProposalStatus } from './types';

/**
 * Which warnings become an exception the manager must justify at submit.
 *
 * Mirrors `EXCEPTION_CODES` / `EXCEPTION_RULES` in the API. The API is authoritative — it
 * refuses a submit with `justification_required` and returns the offending issues in
 * `details` — so this map only exists to ask *before* the refusal rather than after. When they
 * disagree the backstop still works; the manager just sees the question one step later.
 */
export const JUSTIFIABLE: Record<string, string> = {
  below_participation_threshold: 'R-13',
  retrospective_event: 'R-22',
  claim_late: 'R-24',
  attendees_not_pre_vetted: 'R-26',
};

export interface PartitionedIssues {
  /** Severity `error`: submission is refused until these are gone. */
  blocking: Issue[];
  /** Warnings that need a written justification, keyed by the rule they cite. */
  needJustification: Array<Issue & { rule: string }>;
  /** Advisory only — worth showing, never in the way. */
  advisory: Issue[];
}

export function partitionIssues(issues: readonly Issue[]): PartitionedIssues {
  const blocking: Issue[] = [];
  const needJustification: Array<Issue & { rule: string }> = [];
  const advisory: Issue[] = [];

  for (const issue of issues) {
    if (issue.severity === 'error') {
      blocking.push(issue);
      continue;
    }
    const rule = JUSTIFIABLE[issue.code];
    if (rule) needJustification.push({ ...issue, rule });
    else advisory.push(issue);
  }
  return { blocking, needJustification, advisory };
}

/**
 * Why this attendee contributes nothing — in words, not a code.
 *
 * "*resigned 12 Mar — contributes S$0, may still attend*" is the sentence the screens owe a
 * manager: it says what happened, what it costs, and — crucially — that the person is still
 * welcome. R-17 excludes their *entitlement*, never their attendance.
 */
export function describeNonContribution(
  reason: string | null,
  context: { lastDay?: string | null; hireDate?: string | null } = {},
): string {
  switch (reason) {
    case 'exited':
      return context.lastDay
        ? `left ${context.lastDay} — contributes S$0, may still attend (R-17)`
        : 'no longer employed — contributes S$0, may still attend (R-17)';
    case 'serving_notice':
      return 'serving notice — contributes S$0, may still attend (R-08)';
    case 'under_three_months':
      return context.hireDate
        ? `joined ${context.hireDate}, under three months — contributes S$0 (R-04)`
        : 'under three months of service — contributes S$0 (R-04)';
    case 'contributes':
    case null:
      return '';
    default:
      return `${reason.replace(/_/g, ' ')} — contributes S$0`;
  }
}

export type Tone = 'neutral' | 'progress' | 'good' | 'warn' | 'bad';

interface StatusLook {
  label: string;
  tone: Tone;
  /** What the status *means* for the reader, not a restatement of its name. */
  hint: string;
}

export const PROPOSAL_STATUS: Record<ProposalStatus, StatusLook> = {
  draft: { label: 'Draft', tone: 'neutral', hint: 'Not submitted. Only you can see it.' },
  sponsor_review: {
    label: 'With Sponsor',
    tone: 'progress',
    hint: 'Waiting on your department head to endorse it.',
  },
  hr_review: { label: 'With HR', tone: 'progress', hint: 'Waiting on HR to confirm the budget.' },
  clarification_requested: {
    label: 'Question asked',
    tone: 'warn',
    hint: 'Someone needs an answer before this can move.',
  },
  returned: {
    label: 'Returned',
    tone: 'warn',
    hint: 'Sent back for changes. Edit it and submit again.',
  },
  confirmed: {
    label: 'Confirmed',
    tone: 'good',
    hint: 'Budget reserved. Raise the claim within 14 days of the event.',
  },
  claim_due: { label: 'Claim due', tone: 'warn', hint: 'The event has happened. File the claim.' },
  claim_overdue: {
    label: 'Claim overdue',
    tone: 'bad',
    hint: 'Past the 14-day window. The reservation still stands until HR closes it (D-36).',
  },
  claimed: { label: 'Claimed', tone: 'good', hint: 'A claim has been approved against this.' },
  rejected: { label: 'Rejected', tone: 'bad', hint: 'Not approved. The reason is on the record.' },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    hint: 'Withdrawn or cancelled. Any reservation has been released.',
  },
  expired: {
    label: 'Expired',
    tone: 'neutral',
    hint: 'Closed out by HR without a claim. The reservation was released.',
  },
};

export const CLAIM_STATUS: Record<ClaimStatus, StatusLook> = {
  draft: { label: 'Draft', tone: 'neutral', hint: 'Not submitted. Only you can see it.' },
  sponsor_review: {
    label: 'With Sponsor',
    tone: 'progress',
    hint: 'Waiting on your department head to endorse it.',
  },
  returned: {
    label: 'Returned',
    tone: 'warn',
    hint: 'Sent back for changes. Fix it and submit again.',
  },
  clarification_requested: {
    label: 'Question asked',
    tone: 'warn',
    hint: 'Someone needs an answer before this can move.',
  },
  hr_verify: {
    label: 'HR checking',
    tone: 'progress',
    hint: 'HR is checking the figures and the evidence.',
  },
  hr_director: {
    label: 'With HR Director',
    tone: 'progress',
    hint: 'Verified. Waiting on final approval, which only an HR Director can give (D-39).',
  },
  approved: {
    label: 'Approved',
    tone: 'good',
    hint: 'Approved and settled. Waiting on the reimbursement to be paid.',
  },
  paid: { label: 'Paid', tone: 'good', hint: 'Reimbursed. The reference is on the record.' },
  rejected: { label: 'Rejected', tone: 'bad', hint: 'Not approved. The reason is on the record.' },
};

/** Button copy per action, so the same verb never appears with two different meanings. */
export const ACTION_LABEL: Record<string, string> = {
  submit: 'Submit',
  withdraw: 'Withdraw',
  answer_clarification: 'Answer',
  sponsor_endorse: 'Endorse',
  sponsor_return: 'Return for changes',
  sponsor_reject: 'Reject',
  request_clarification: 'Ask a question',
  hr_return: 'Return for changes',
  hr_reject: 'Reject',
  hr_verify: 'Pass verification',
  amend_attendees: 'Amend namelist',
  confirm: 'Confirm budget',
  reject: 'Reject',
  reopen: 'Reopen',
  cancel: 'Cancel event',
  expire: 'Close out',
  director_approve: 'Approve and settle',
  director_return: 'Return for changes',
  director_reject: 'Reject',
  mark_paid: 'Mark paid',
};

/**
 * The activity types R-11 allows. A closed list, because "team dinner" and "Team Dinner" and
 * "dinner" would otherwise all appear in the same report.
 */
export const ACTIVITY_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'team_dinner', label: 'Team dinner' },
  { value: 'team_lunch', label: 'Team lunch' },
  { value: 'sports', label: 'Sports or recreation' },
  { value: 'outing', label: 'Outing or excursion' },
  { value: 'workshop', label: 'Workshop or talk' },
  { value: 'festive', label: 'Festive celebration' },
  { value: 'other', label: 'Other (say what in the remark)' },
];

export const activityLabel = (value: string): string =>
  ACTIVITY_TYPES.find((type) => type.value === value)?.label ??
  value.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase());

export const PAYMENT_MODES: ReadonlyArray<{ value: string; label: string; needs: string | null }> =
  [
    { value: 'personal', label: 'Paid personally, to be reimbursed', needs: 'payee' },
    { value: 'corporate_card', label: 'Corporate card', needs: 'cardholder' },
    { value: 'other', label: 'Other', needs: 'description' },
  ];

/** `role` → what to call it on screen. `staff` is the default for anyone with no grant. */
export const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Super admin',
  admin: 'HR admin',
  dept_head: 'Department head (Sponsor)',
  dept_manager: 'Department manager',
  staff: 'Staff',
};
